import React from 'react';
import { useApolloClient } from '@apollo/client';

import Button, { BUTTON_SIZES, BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import OnboardingFundSuggestionCard from 'buyside/components/presentation/Onboarding/OnboardingForm/OnboardingCommunitySuggestionCard/onboardingFundSuggestionCard';
import OnboardingFundSuggestionCardLoading from 'buyside/components/presentation/Onboarding/OnboardingForm/OnboardingCommunitySuggestionCard/onboardingFundSuggestionCardLoading';

const CurrentUserWatchlist = require('buyside/graphql/queries/CurrentUserWatchlist');

import {
  CurrentUserWatchlistQuery,
  useOnboardingFundRecommendationsQuery
} from 'interfaces/graphql-typings';

import styles from './onboardingCommunitySuggestionsStyles.scss';

const OnboardingFundSuggestions: React.FC = (): React.ReactElement => {
  const { data, loading, error, refetch, variables } = useOnboardingFundRecommendationsQuery({
    variables: { offset: 0 }
  });
  const currentPage = variables?.offset || 0;

  const recommendedFunds = data?.currentUser?.onboardingFundRecommendations;
  const maxPage = data?.currentUser?.onboardingFundRecommendationMaxOffset;

  const client = useApolloClient();
  const currentUserWatchlist = client.readQuery<CurrentUserWatchlistQuery>({
    query: CurrentUserWatchlist
  })?.currentUser?.watchlist;

  if (error) throw error;

  return (
    <div>
      <h3 className={styles.fieldHeader}>Recommended Funds</h3>
      {loading && (
        <div className={styles.indexArea}>
          {[1, 2, 3].map(el => (
            <OnboardingFundSuggestionCardLoading key={el} />
          ))}
        </div>
      )}
      {!loading && !recommendedFunds?.length && (
        <div>
          No recommendations found. Adjust your preferences for fund types, strategies, and AUM
          ranges to see new recommendations.
        </div>
      )}
      <div className={styles.indexArea}>
        {recommendedFunds?.map(fund => {
          if (!fund) return null;
          return (
            <OnboardingFundSuggestionCard
              key={fund?.id}
              fund={fund}
              preferredAumRanges={
                currentUserWatchlist?.aumRanges?.map(aum => (aum ? aum.name : null)) || []
              }
              preferredFundTypes={
                currentUserWatchlist?.fundTypes?.map(type => (type ? type.name : null)) || []
              }
              preferredStrategies={
                currentUserWatchlist?.fundStrategies?.map(str => (str ? str.name : null)) || []
              }
            />
          );
        })}
      </div>
      <div className={styles.buttonArea}>
        <Button
          disabled={currentPage === 0}
          isLoading={loading}
          onClick={(): void => {
            refetch({ offset: currentPage - 1 });
          }}
          size={BUTTON_SIZES.SMALL}
          text="<< Previous Page"
          type={BUTTON_TYPES.LINK}
        />
        <Button
          disabled={currentPage === maxPage}
          isLoading={loading}
          onClick={(): void => {
            refetch({ offset: currentPage + 1 });
          }}
          size={BUTTON_SIZES.SMALL}
          text="Show More >>"
          type={BUTTON_TYPES.LINK}
        />
      </div>
    </div>
  );
};

export default OnboardingFundSuggestions;
